'use client';

import { cn, Logo, Slot, Text, useSlots } from '@magicschool/spellbook';
import type { Language } from '@magicschool/supabase/types';
import { cva, type VariantProps } from 'class-variance-authority';
import NextImage from 'next/image';
import type { ComponentProps, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import LocalizationDropdownMenu from '@/components/LocalizationDropdownMenu';

const authContainerContentVariants = cva('w-full flex flex-col items-center gap-6', {
  variants: {
    size: {
      xs: 'max-w-xs',
      sm: 'max-w-sm',
      full: 'max-w-full',
    },
  },
  defaultVariants: {
    size: 'full',
  },
});

const AuthContainerIllustrationSlot = Slot<ComponentProps<typeof NextImage>>({
  displayName: 'AuthContainer.Illustration',
  renderDefault: (props) => <NextImage {...props} />,
});

const AuthContainerContentSlot = Slot<ComponentProps<'div'> & VariantProps<typeof authContainerContentVariants>>({
  displayName: 'AuthContainer.Content',
  renderDefault: ({ size, className, children, ...props }) => (
    <div className={cn(authContainerContentVariants({ size }), className)} {...props}>
      {children}
    </div>
  ),
});

const AuthContainerFooterSlot = Slot<{
  autoLocale?: Language | null;
  analyticsEventName?: boolean;
}>({
  displayName: 'AuthContainer.Footer',
  preventOverride: true,
  renderDefault: ({ autoLocale, analyticsEventName }) => (
    <div className="flex flex-col items-center gap-2">
      <Text>
        <FormattedMessage id="signup-role.localization-tag" />
      </Text>
      <LocalizationDropdownMenu autoLocale={autoLocale} analyticsEventName={analyticsEventName} />
    </div>
  ),
});

/**
 * Centered page-level container for auth flows. Always renders the
 * MagicSchool logo at the top, then an optional illustration, the main
 * content area, and a localization footer.
 *
 * Accepts `className` and all native `<div>` props — forwarded to the outer
 * wrapper.
 *
 * ### Composition (slots)
 * - `<AuthContainer.Illustration>`: Renders a `next/image` with the provided
 *   props. Optional — omit for pages with no hero image.
 * - `<AuthContainer.Content>`: Centered column that wraps form content. Use
 *   the `size` prop (`xs | sm | full`) to control max-width; defaults to
 *   `full`. Each auth page should supply its own `size` based on content width.
 * - `<AuthContainer.Footer>`: Renders the localization dropdown. Accepts
 *   `autoLocale` and `analyticsEventName`. `preventOverride: true` — the
 *   default localization UI cannot be replaced.
 */
function AuthContainerBase({ children, className, ...props }: ComponentProps<'div'>) {
  const { slots } = useSlots(children as ReactNode, {
    illustration: { Component: AuthContainerIllustrationSlot },
    content: { Component: AuthContainerContentSlot },
    footer: { Component: AuthContainerFooterSlot },
  });

  return (
    <div
      className={cn('self-start w-full mx-auto max-w-3xl flex pt-12 flex-col items-center gap-12', className)}
      data-spellbook-name="auth-container"
      {...props}
    >
      <Logo className="w-full max-h-10" />
      {slots.illustration}
      {slots.content}
      {slots.footer}
    </div>
  );
}

export const AuthContainer = Object.assign(AuthContainerBase, {
  Illustration: AuthContainerIllustrationSlot,
  Content: AuthContainerContentSlot,
  Footer: AuthContainerFooterSlot,
});
